import React, {useEffect, useMemo, useState} from "react";
import IbatApi from "../../utils/IbatApi";
import {useSnackbar} from "notistack";
import * as XLSX from "xlsx";
import {getColor} from "../../helpers/interventionManager";


export default function Sites() {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar()

    const [state, setState] = useState({
        siteList: [],
        elementList: []
    })
    const [elementFilters, setElementFilters] = useState({});
    const [nameFilter, setNameFilter] = useState("");
    const [shopFilter, setShopFilter] = useState("");
    const [addressFilter, setAddressFilter] = useState("");
    const [siretFilter, setSiretFilter] = useState("");
    const [roofCreationFilter, setRoofCreationFilter] = useState("");
    const [roofSurfaceFilter, setRoofSurfaceFilter] = useState("");
    const [roofMaintenanceFilter, setRoofMaintenanceFilter] = useState("");
    const [networkDesiltingFilter, setNetworkDesiltingFilter] = useState("");
    const [lastInterventionDateFilter, setLastInterventionDateFilter] = useState("");
    const [epcFilter, setEpcFilter] = useState("");
    const [roofAccessFilter, setRoofAccessFilter] = useState("");
    const [roofTypeFilter, setRoofTypeFilter] = useState("");
    const [operationType, setOperationType] = useState([]);
    const [loading, setloading] = useState(false)
    let api = new IbatApi();
    useEffect(() => {
        getSites()
    }, [])

    // Toggle a filter for a given element and level (0,1,2,3,4 or null)
    // Now supports multi-select per column: clicking toggles inclusion in an array.
    function toggleElementFilter(element, level) {
        setElementFilters(prev => {
            const current = prev[element] || [];
            let next;
            if (current.includes(level)) {
                // remove level
                next = current.filter(l => l !== level);
            } else {
                // add level
                next = [...current, level];
            }
            if (next.length === 0) {
                // remove key if empty to treat as no filter for this column
                const {[element]: _, ...rest} = prev;
                return rest;
            }
            return {...prev, [element]: next};
        })
    }

    function clearElementFilter(element) {
        setElementFilters(prev => {
            const {[element]: _, ...rest} = prev;
            return rest;
        })
    }

    const filteredSiteList = useMemo(() => {
        if (!state.siteList || state.siteList.length === 0) return [];

        let result = state.siteList;

        // 🔍 filtrage par nom
        if (nameFilter) {
            result = result.filter(site =>
                site.name?.toLowerCase().includes(nameFilter.toLowerCase())
            );
        }

        if (shopFilter) {
            result = result.filter(site =>
                site.name?.includes(shopFilter)
            );
        }

        // 🔍 filtrage par adresse
        if (addressFilter) {
            result = result.filter(site =>
                site.address?.toLowerCase().includes(addressFilter.toLowerCase())
            );
        }

        // 🔍 filtrage par siret
        if (siretFilter) {
            result = result.filter(site =>
                site.siret?.toLowerCase().includes(siretFilter.toLowerCase())
            );
        }

        // 🔍 filtrage par date de couverture
        if (roofCreationFilter) {
            result = result.filter(site => {
                if (!site.roofCreation) return false;
                const siteDate = new Date(site.roofCreation).toISOString().split('T')[0];
                return siteDate === roofCreationFilter;
            });
        }

        // 🔍 filtrage par surface de couverture
        if (roofSurfaceFilter) {
            result = result.filter(site =>
                site.roofSurface?.toString().includes(roofSurfaceFilter)
            );
        }

        // 🔍 filtrage par entretien annuel
        if (roofMaintenanceFilter) {
            result = result.filter(site =>
                site.roofMaintenanceRate?.toString().toLowerCase().includes(roofMaintenanceFilter.toLowerCase())
            );
        }

        // 🔍 filtrage par dernier curage
        if (networkDesiltingFilter) {
            result = result.filter(site => {
                if (!site.networkDesiltingDate) return false;
                const siteDate = new Date(site.networkDesiltingDate).toISOString().split('T')[0];
                return siteDate === networkDesiltingFilter;
            });
        }

        // 🔍 filtrage par dernière date d'intervention
        if (lastInterventionDateFilter) {
            result = result.filter(site => {
                if (!site.lastInterventionDate) return false;
                const siteDate = new Date(site.lastInterventionDate).toISOString().split('T')[0];
                return siteDate === lastInterventionDateFilter;
            });
        }

        // 🔍 filtrage par EPC (exact match)
        if (epcFilter) {
            result = result.filter(site => site.epc === epcFilter);
        }

        // 🔍 filtrage par Accès au toit (exact match)
        if (roofAccessFilter) {
            result = result.filter(site => site.roofAccess === roofAccessFilter);
        }

        // 🔍 filtrage par Type de toit (exact match)
        if (roofTypeFilter) {
            result = result.filter(site => site.roofType === roofTypeFilter);
        }

        // 🔍 filtrage par éléments
        const filterKeys = Object.keys(elementFilters);
        if (filterKeys.length === 0) return result;

        return result.filter(site => {
            for (const el of filterKeys) {
                const selected = elementFilters[el];
                const value = site[el];
                if (selected.includes(null)) {
                    if (value === null || value === undefined) continue;
                    const otherSelected = selected.filter(l => l !== null);
                    if (otherSelected.length > 0 && otherSelected.includes(value)) continue;
                    return false;
                } else {
                    if (!selected.includes(value)) return false;
                }
            }
            return true;
        });
    }, [state.siteList, elementFilters, nameFilter, shopFilter, addressFilter, siretFilter, roofCreationFilter, roofSurfaceFilter, roofMaintenanceFilter, networkDesiltingFilter, epcFilter, roofAccessFilter, roofTypeFilter, lastInterventionDateFilter]);

    const categoryColors = {
        tenant: "rgba(162,218,255,0.4)",
        landlord: "rgba(252,191,122,0.4)"
    };

    function transformSiteData(siteList) {
        const siteMap = new Map();
        const typesSet = new Set(); // Collecter les types en même temps

        siteList.forEach(site => {
            const siteId = site.id;

            // Collecter le type ID
            if (site.typeId) {
                typesSet.add(site.typeId);
            }

            if (!siteMap.has(siteId)) {
                // Créer l'objet de base pour ce site
                const baseSite = {...site};
                delete baseSite.typeId;
                delete baseSite.typeName;
                delete baseSite.stateLevel;

                siteMap.set(siteId, baseSite);
            }

            // Assigner la valeur stateLevel au type ID correspondant
            const existingSite = siteMap.get(siteId);
            existingSite[site.typeId] = site.stateLevel;
        });

        // Retourner les sites transformés ET les types
        return {
            sites: Array.from(siteMap.values()),
            elements: Array.from(typesSet).sort()
        };
    }

    function getSites() {
        setloading(true)
        const result = api.getSiteFilterd({
            ...state.filters,
            pageNumber: state.pageNumber,
            pageSize: state.pageSize
        })
        result
            .then(res => {
                setloading(false)
                const transformedSites = transformSiteData(res);

                setState(state => ({
                    ...state,
                    siteList: transformedSites.sites,
                    elementList: transformedSites.elements
                }))
            })
            .catch(e => {
                setloading(false)
                enqueueSnackbar(e.message, {variant: "error"})
            })

        api.getAllOperationType()
            .then(res => setOperationType(res))
    }


    const exportToExcel = () => {
        const wb = XLSX.utils.book_new();

        // Même tri des opérations que l'affichage
        const sortByClientOrder = (a, b) => {
            const orderA = a.clientOrder ? parseInt(a.clientOrder) : Infinity;
            const orderB = b.clientOrder ? parseInt(b.clientOrder) : Infinity;
            return orderA - orderB;
        };
        const orderedOps = [
            ...[...activeOperations.general].sort(sortByClientOrder),
            ...[...activeOperations.tenant].sort(sortByClientOrder),
            ...[...activeOperations.landlord].sort((a, b) => a.name.localeCompare(b.name))
        ];

        const formatDate = d => d ? new Date(d).toISOString().split('T')[0] : "";
        const formatLocaleDate = d => d ? new Date(d).toLocaleDateString() : "";
        const formatShared = site => {
            const parts = [];
            if (site.sharedWithNames) parts.push(`↗ ${site.sharedWithNames}`);
            if (site.sharedByNames) parts.push(`↙ ${site.sharedByNames}`);
            return parts.length ? parts.join(" | ") : "-";
        };

        const sheetData = [];

        const headers = [
            "Nom du site",
            "Magasin",
            "Adresse",
            "Siret",
            "Date de la couverture",
            "Surface de la couverture",
            "Entretien annuel (couverture)",
            "EPC",
            "Accès au toit",
            "Type de toit",
            "Dernier curage",
            "Date de la dernière commande",
            "Partagé avec",
            "Maintenance externe",
            ...orderedOps.map(op => op.name)
        ];
        sheetData.push(headers);

        // On itère sur filteredSiteList pour refléter exactement le tableau affiché
        filteredSiteList.forEach(site => {
            const row = [
                site.name || "",
                site.name.replace(/\D/g, "") || "",
                site.address || "",
                site.siret || "",
                formatDate(site.roofCreation),
                site.roofSurface ? `${site.roofSurface} m²` : "",
                site.roofMaintenanceRate ?? "",
                site.epc || "N/A",
                site.roofAccess || "N/A",
                site.roofType || "N/A",
                formatDate(site.networkDesiltingDate),
                formatLocaleDate(site.lastInterventionDate),
                formatShared(site),
                site.externalMaintenance ? "Oui" : "Non",
                ...orderedOps.map(op => {
                    const value = site[op.id];
                    return (value !== undefined && value !== null) ? value.toString() : "N/A";
                })
            ];
            sheetData.push(row);
        });

        const ws = XLSX.utils.aoa_to_sheet(sheetData);

        const colWidths = [
            {wch: 20}, // Nom du site
            {wch: 30}, // Adresse
            {wch: 15}, // Siret
            {wch: 15}, // Date de la couverture
            {wch: 18}, // Surface de la couverture
            {wch: 22}, // Entretien annuel (couverture)
            {wch: 10}, // EPC
            {wch: 15}, // Accès au toit
            {wch: 15}, // Type de toit
            {wch: 15}, // Dernier curage
            {wch: 22}, // Date de la dernière commande
            {wch: 25}, // Partagé avec
            {wch: 18}, // Maintenance externe
            ...orderedOps.map(() => ({wch: 18}))
        ];
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, "Sites");

        const fileName = `Sites_Export_${new Date().toISOString().split('T')[0]}.xls`;
        XLSX.writeFile(wb, fileName, {bookType: 'xls'});
    };

    const updateDate = (siteId, e, route) => {
        const data = e.target.value;

        const dateRegex = /^(16\d{2}|17\d{2}|18\d{2}|19\d{2}|20\d{2}|21\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
        if (!data || !dateRegex.test(data)) {
            return;
        }

        const value = data.split("-");
        const newDate = Date.UTC( value[0], value[1] - 1, value[2])
        api.updateDate(siteId, newDate, route)
    }
    const updateExternalMaintenance = (siteId, e, route) => {
        api.updateExternalMaintenanceSite(siteId, e.target.checked)
    }

    const categorizedOperations = useMemo(() => {

        if (!operationType || operationType.length === 0) {
            return { general: [], tenant: [], landlord: [] };
        }

        // Initialisation des groupes
        const groups = {
            general: [],
            tenant: [],
            landlord: []
        };

        // Répartition
        operationType.forEach(op => {
            let key;
            if (!op.appliesTo || op.appliesTo === "general") {
                key = "general";
            } else if (op.appliesTo === "tenant") {
                key = "tenant";
            } else if (op.appliesTo === "landlord") {
                key = "landlord";
            } else {
                key = "general"; // fallback
            }

            groups[key].push(op);
        });

        // Tri alphabétique
        Object.keys(groups).forEach(k => {
            groups[k].sort((a, b) => a.name.localeCompare(b.name));
        });
        return groups;
    }, [operationType]);

    const activeOperations = useMemo(() => {
        if (!categorizedOperations || !state.siteList || state.siteList.length === 0)
            return { general: [], tenant: [], landlord: [] };

        // Fonction utilitaire pour vérifier si une opération a des données sur au moins un site
        const isOperationUsed = (opId) => {
            return state.siteList.some(site => site[opId] !== null && site[opId] !== undefined);
        };

        return {
            general: categorizedOperations.general.filter(op => isOperationUsed(op.id)),
            tenant: categorizedOperations.tenant.filter(op => isOperationUsed(op.id)),
            landlord: categorizedOperations.landlord.filter(op => isOperationUsed(op.id))
        };
    }, [categorizedOperations, state.siteList]);

    // Extract unique values for dropdown filters
    const uniqueEpcValues = useMemo(() => {
        if (!state.siteList || state.siteList.length === 0) return [];
        const values = state.siteList
            .map(site => site.epc)
            .filter(val => val !== null && val !== undefined && val !== "N/A")
            .filter((val, index, self) => self.indexOf(val) === index)
            .sort();
        return values;
    }, [state.siteList]);

    const uniqueRoofAccessValues = useMemo(() => {
        if (!state.siteList || state.siteList.length === 0) return [];
        const values = state.siteList
            .map(site => site.roofAccess)
            .filter(val => val !== null && val !== undefined && val !== "N/A")
            .filter((val, index, self) => self.indexOf(val) === index)
            .sort();
        return values;
    }, [state.siteList]);

    const uniqueRoofTypeValues = useMemo(() => {
        if (!state.siteList || state.siteList.length === 0) return [];
        const values = state.siteList
            .map(site => site.roofType)
            .filter(val => val !== null && val !== undefined && val !== "N/A")
            .filter((val, index, self) => self.indexOf(val) === index)
            .sort();
        return values;
    }, [state.siteList]);

    return (
        <div style={{height: "92vh", width: "96vw", margin: "auto"}}>

            <div style={{display: "flex", flexDirection: "column", gap: ".5rem", width: "100%", height: "100%"}}>

                <button
                    onClick={exportToExcel}
                    style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: "#4CAF50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer"
                    }}
                >
                    Exporter en Excel
                </button>

                <div style={{
                    overflow: "auto",
                }}>
                    <table style={{
                        borderCollapse: "collapse"
                    }}>
                        <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        {/* Ligne de regroupement des catégories */}
                        <tr style={{ textAlign: "center" }}>
                            <th colSpan={13}></th>
                            {activeOperations.general.length > 0 && (
                                <th
                                    colSpan={activeOperations.general.length}
                                    style={{ backgroundColor: "rgba(123,252,94,0.4)", border: "1px solid #ddd", fontWeight: "bold" }}
                                >
                                    Général
                                </th>
                            )}
                            {activeOperations.tenant.length > 0 && (
                                <th
                                    colSpan={activeOperations.tenant.length}
                                    style={{ backgroundColor: categoryColors["tenant"] || "rgba(123,252,94,0.4)", border: "1px solid #ddd", fontWeight: "bold" }}
                                >
                                    Locataire
                                </th>
                            )}
                            {activeOperations.landlord.length > 0 && (
                                <th
                                    colSpan={activeOperations.landlord.length}
                                    style={{ backgroundColor: categoryColors["landlord"] || "rgba(123,252,94,0.4)", border: "1px solid #ddd", fontWeight: "bold" }}
                                >
                                    Bailleur
                                </th>
                            )}
                        </tr>

                        <tr style={{ backgroundColor: "#f2f2f2" }}>
                            <th
                                style={{
                                    border: "1px solid #ddd",
                                    padding: "8px",
                                    textAlign: "left",
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 2,
                                }}
                            >
                                <b>Nom du site</b>
                                <input
                                    type="text"
                                    placeholder="Filtrer par nom..."
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>magasin</b>
                                <input
                                    type="text"
                                    placeholder="Filtrer..."
                                    value={shopFilter}
                                    onChange={(e) => setShopFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Adresse</b>
                                <input
                                    type="text"
                                    placeholder="Filtrer..."
                                    value={addressFilter}
                                    onChange={(e) => setAddressFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Siret</b>
                                <input
                                    type="text"
                                    placeholder="Filtrer..."
                                    value={siretFilter}
                                    onChange={(e) => setSiretFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Date de la couverture</b>
                                <input
                                    type="date"
                                    value={roofCreationFilter}
                                    onChange={(e) => setRoofCreationFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Surface de la couverture</b>
                                <input
                                    type="text"
                                    placeholder="Filtrer..."
                                    value={roofSurfaceFilter}
                                    onChange={(e) => setRoofSurfaceFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Entretien annuel (couverture)</b>
                                <input
                                    type="text"
                                    placeholder="Filtrer..."
                                    value={roofMaintenanceFilter}
                                    onChange={(e) => setRoofMaintenanceFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>EPC</b>
                                <select
                                    value={epcFilter}
                                    onChange={(e) => setEpcFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                >
                                    <option value="">Tous</option>
                                    {uniqueEpcValues.map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Accès au toit</b>
                                <select
                                    value={roofAccessFilter}
                                    onChange={(e) => setRoofAccessFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                >
                                    <option value="">Tous</option>
                                    {uniqueRoofAccessValues.map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Type de toit</b>
                                <select
                                    value={roofTypeFilter}
                                    onChange={(e) => setRoofTypeFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                >
                                    <option value="">Tous</option>
                                    {uniqueRoofTypeValues.map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Dernier curage</b>
                                <input
                                    type="date"
                                    value={networkDesiltingFilter}
                                    onChange={(e) => setNetworkDesiltingFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Date de la dernière commande</b>
                                <input
                                    type="date"
                                    value={lastInterventionDateFilter}
                                    onChange={(e) => setLastInterventionDateFilter(e.target.value)}
                                    style={{
                                        padding: "4px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%"
                                    }}
                                />
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Partagé avec</b>
                            </th>
                            <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>
                                <b>Maintenance externe</b>
                            </th>

                            {/* Colonnes dynamiques avec filtres (reprend ton code d'origine) */}
                            {[
                                ...activeOperations.general?.sort((a, b) => {
                                    const orderA = a.clientOrder ? parseInt(a.clientOrder) : Infinity;
                                    const orderB = b.clientOrder ? parseInt(b.clientOrder) : Infinity;
                                    return orderA - orderB;
                                }),
                                ...activeOperations.tenant?.sort((a, b) => {
                                    const orderA = a.clientOrder ? parseInt(a.clientOrder) : Infinity;
                                    const orderB = b.clientOrder ? parseInt(b.clientOrder) : Infinity;
                                    return orderA - orderB;
                                }),
                                ...activeOperations?.landlord?.sort((a, b) => a.name.localeCompare(b.name))
                            ].map(op => (
                                <th
                                    key={op.id}
                                    style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", backgroundColor: categoryColors[op.appliesTo] || "rgba(123,252,94,0.4)", }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 8
                                        }}
                                    >
                                        <span>{op.name}</span>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            {[0, 1, 2, 3, 4, null].map(level => (
                                                <button
                                                    key={String(level)}
                                                    title={`Filtrer: ${level === null ? 'Aucun' : level}`}
                                                    onClick={() => toggleElementFilter(op.id, level)}
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: '50%',
                                                        border: elementFilters[op.id]?.includes(level)
                                                            ? '2px solid #000'
                                                            : '1px solid #bbb',
                                                        padding: 0,
                                                        cursor: 'pointer',
                                                        backgroundColor: getColor(level)?.backgroundColor
                                                    }}
                                                />
                                            ))}
                                            {elementFilters[op.id] !== undefined && (
                                                <button
                                                    title="Effacer le filtre"
                                                    onClick={() => clearElementFilter(op.id)}
                                                    style={{
                                                        marginLeft: 4,
                                                        border: '1px solid #bbb',
                                                        background: '#fff',
                                                        color: '#333',
                                                        padding: '0 4px',
                                                        borderRadius: 4,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                        </thead>

                        <tbody>
                        {filteredSiteList.map(site => (
                            <tr key={site.id}>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center", position: "sticky", left: 0, backgroundColor: "#f2f2f2"}}>
                                    <a target={"_blank"} href={`/site/${site.id}`}>{site.name}</a>
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.name.replace(/\D/g, "")}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.address}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.siret}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    <input
                                        type={"date"}
                                        defaultValue={site.roofCreation ? new Date(site.roofCreation).toISOString().split('T')[0] : null}
                                        onChange={(e) => updateDate(site.id, e, "updateRoofCreation")}

                                    />
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.roofSurface && `${site.roofSurface} m²`}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.roofMaintenanceRate}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.epc || "N/A"}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.roofAccess || "N/A"}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.roofType || "N/A"}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    <input
                                        type={"date"}
                                        defaultValue={site.networkDesiltingDate ? new Date(site.networkDesiltingDate).toISOString().split('T')[0] : null}
                                        onChange={(e) => updateDate(site.id, e, "updateNetworkDesiltingDate")}
                                    />
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.lastInterventionDate && new Date(site.lastInterventionDate).toLocaleDateString()}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    {site.sharedWithNames && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span style={{ fontSize: "18px" }}>↗️</span>
                                            <span>{site.sharedWithNames}</span>
                                        </div>
                                    )}
                                    {site.sharedByNames && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: site.sharedWithNames ? "4px" : "0" }}>
                                            <span style={{ fontSize: "18px" }}>↙️</span>
                                            <span>{site.sharedByNames}</span>
                                        </div>
                                    )}
                                    {!site.sharedWithNames && !site.sharedByNames && "-"}
                                </td>
                                <td style={{border: "1px solid #ddd", padding: "8px", textAlign: "center"}}>
                                    <input
                                        type={"checkbox"}
                                        defaultChecked={site.externalMaintenance ? true : false}
                                        onChange={(e) => updateExternalMaintenance(site.id, e)}
                                    />
                                </td>
                                {/* Mapper les valeurs des éléments */}
                                {/*state.elementList.map(element => (
                                    <td key={element} style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        background: site[element] !== undefined && site[element] !== null ? undefined : "rgba(170,170,170,0.67)"
                                    }}>
                                        <div style={{
                                            ...getColor(site[element]),
                                            margin: "auto",
                                            borderRadius: "50%",
                                            width: 40,
                                            height: 40,
                                            border: "3px solid white"
                                        }}/>
                                    </td>
                                ))*/}

                                {[
                                    ...activeOperations.general?.sort((a, b) => {
                                        const orderA = a.clientOrder ? parseInt(a.clientOrder) : Infinity;
                                        const orderB = b.clientOrder ? parseInt(b.clientOrder) : Infinity;
                                        return orderA - orderB;
                                    }),
                                    ...activeOperations.tenant?.sort((a, b) => {
                                        const orderA = a.clientOrder ? parseInt(a.clientOrder) : Infinity;
                                        const orderB = b.clientOrder ? parseInt(b.clientOrder) : Infinity;
                                        return orderA - orderB;
                                    }),
                                    ...activeOperations?.landlord?.sort((a, b) => a.name.localeCompare(b.name))
                                ].map(op => (
                                    <td
                                        key={op.id}
                                        style={{
                                            border: "1px solid #ddd",
                                            padding: "8px",
                                            backgroundColor: categoryColors[op.appliesTo] || "rgba(123,252,94,0.4)",
                                        }}
                                    >
                                        <div
                                            style={{
                                                ...getColor(site[op.id]),
                                                margin: "auto",
                                                borderRadius: "50%",
                                                width: 40,
                                                height: 40,
                                                border: "3px solid white"
                                            }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}

                    </tbody>
                    </table>
                </div>

            </div>
        </div>
    )
}
